import { ChangeDetectionStrategy, Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA, MatLegacyDialogModule, MatLegacyDialogRef } from '@angular/material/legacy-dialog';

import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { EncColor, EncField, EncSeries, Encoding, VariableType } from '@mode-vegas/vegas';
import { AccountFacade } from '@mode/accounts/contract';
import { ButtonComponent, IconButtonComponent, IconComponent, TooltipDirective } from '@mode/capra';
import { ColorsModalData, ColorsModalFacade } from '@mode/charts/contract';
import { EncodingHelpers, addFieldColor, computePalette, resolveFieldColor } from '@mode/charts/util-vegas';
import { ColorPalettePreviewSwatchComponent } from '@mode/colors/ui';
import { ChartTypes, ConditionalFormattingTypes, FlamingoTypes, OrgTypes } from '@mode/shared/contract-common';
import { DialogHeaderComponent, LabeledMenuComponent, MenuItem } from '@mode/shared/ui';
import { generateUUID } from '@mode/shared/util-js';
import { CompiledField } from '@mode/shared/util-switch';
import { FieldColor } from 'libs/shared/contract-common/src/lib/charts';
import { Observable, Subject, takeUntil } from 'rxjs';

interface Columns {
  legendId: string;
  addToText: boolean;
  highlighted: boolean;
  color?: FieldColor;
  selected?: ConditionalFormattingTypes.Column;
  selectedColorPalette?: OrgTypes.ColorPalette;
}

@Component({
  standalone: true,
  selector: 'mode-conditional-formatting-dialog',
  templateUrl: './conditional-formatting-dialog.component.html',
  imports: [
    ButtonComponent,
    CommonModule,
    DialogHeaderComponent,
    IconComponent,
    DragDropModule,
    MatLegacyDialogModule,
    LabeledMenuComponent,
    TooltipDirective,
    IconButtonComponent,
    ColorPalettePreviewSwatchComponent,
  ],
  styleUrls: ['./conditional-formatting-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class ConditionalFormattingDialogComponent implements OnInit, OnDestroy {
  @Output() apply = new EventEmitter<{ colors: ConditionalFormattingTypes.Color[]; applyClicked: boolean }>();
  @Output() cancel = new EventEmitter<void>();

  private encoding!: Encoding;
  protected destroyed$ = new Subject<void>();

  model!: FlamingoTypes.ModeModel;
  view$: Observable<FlamingoTypes.ModeView | undefined> | undefined;
  view: FlamingoTypes.ModeView | undefined;
  queryToken$: Observable<string> | undefined;
  queryRunToken: string | undefined;
  vizToken$: Observable<string> | undefined;
  vizToken: string | undefined;
  fieldFormatMap$: Observable<ChartTypes.FieldFormatMap> | undefined;
  format!: ChartTypes.ChartFormat;
  defaultColorPalettes: readonly OrgTypes.ColorPalette[] = [];
  customColorPalettes: readonly OrgTypes.ColorPalette[] = [];
  allColorPalettes: readonly OrgTypes.ColorPalette[] = [];
  currentColorPaletteToken: string | undefined;
  fieldColors: ChartTypes.FieldColorMap | undefined;
  selectedColorPalette: OrgTypes.ColorPalette | undefined;
  fieldFormats: ChartTypes.FieldFormatMap | undefined;

  isApplyDisabled = false;
  isChanged = false;
  columnsMap: Record<string, boolean> = {};
  columns: Columns[] = [];
  allColumns: ConditionalFormattingTypes.Column[] = [];
  colorField: FieldColor | undefined;
  isAddColumnDisabled = true;

  constructor(
    private accountFacade: AccountFacade,
    private dialogRef: MatLegacyDialogRef<ConditionalFormattingDialogComponent>,
    @Inject(MAT_LEGACY_DIALOG_DATA) public data: ConditionalFormattingTypes.ConditionalFormattingDialogFormattedData,
    private colorsModalFacade?: ColorsModalFacade
  ) {}

  ngOnInit(): void {
    this.data.observables.token$.pipe(takeUntil(this.destroyed$)).subscribe((token: string) => (this.vizToken = token));
    this.data.observables.queryRunToken$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((token: string) => (this.queryRunToken = token));

    this.data.observables.view$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((view: FlamingoTypes.ModeView) => (this.view = view));

    this.data.observables.model$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((model: FlamingoTypes.ModeModel) => (this.model = model));

    this.data.observables.encoding$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((encoding: Encoding) => (this.encoding = encoding));

    this.data.observables.fieldFormats$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((fieldFormats: ChartTypes.FieldFormatMap) => (this.fieldFormats = fieldFormats));

    this.data.observables.format$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((format: ChartTypes.ChartFormat) => (this.format = format));

    this.data.observables.fieldColors$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((fieldColors: ChartTypes.FieldColorMap) => (this.fieldColors = fieldColors));

    this.data.observables.colorPaletteToken$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((token: string) => (this.currentColorPaletteToken = token));

    this.vizToken$ = this.data.observables.token$;

    this.accountFacade.reportOwnerColorPalettes$.pipe(takeUntil(this.destroyed$)).subscribe((palettes) => {
      this.allColorPalettes = [...palettes].sort((a, b) => a.name.localeCompare(b.name));
      this.defaultColorPalettes = palettes.filter(
        (p: OrgTypes.ColorPalette) =>
          (p.type === OrgTypes.ColorPaletteType.Sequential || p.type === OrgTypes.ColorPaletteType.Divergent) &&
          p.predefined
      );
      this.customColorPalettes = palettes.filter(
        (p: OrgTypes.ColorPalette) =>
          (p.type === OrgTypes.ColorPaletteType.Sequential || p.type === OrgTypes.ColorPaletteType.Divergent) &&
          !p.predefined
      );
    });

    if (this.defaultColorPalettes || this.customColorPalettes) {
      this.selectedColorPalette =
        this.defaultColorPalettes
          .concat(this.customColorPalettes)
          .find((palette) => palette.type === OrgTypes.ColorPaletteType.Sequential && palette.isDefault) ??
        this.defaultColorPalettes[0];
      this.currentColorPaletteToken = this.selectedColorPalette.token;
    }

    if (this.selectedColorPalette) {
      const paletteColors = this.selectedColorPalette.value.split(',').map((color) => color.trim().toUpperCase());
      this.colorField = {
        colorPaletteToken: this.selectedColorPalette.token,
        palette: this.selectedColorPalette.value.split(',').map((color) => color.toUpperCase()),
        map: {},
        gradient: {
          gradientType: ChartTypes.GradientType.SEQUENTIAL,
          colorEntries: {
            [ChartTypes.GradientStep.DATA_MIN]: paletteColors[0],
            [ChartTypes.GradientStep.DATA_MAX]: paletteColors[1],
          },
        },
      };
    }

    this.columns = this.data.colors.map((column) => {
      this.columnsMap[column.selected.value] = true;
      let columnColorPalette = column.selectedColorPalette;
      const fieldColor = column.color ?? this.getColumnFieldColor(column.selected);
      if (columnColorPalette == null) {
        const palette = this.allColorPalettes.find((palette) => palette.token === fieldColor?.colorPaletteToken);
        columnColorPalette = palette ?? this.selectedColorPalette;
      }
      return {
        ...column,
        legendId: column.legendId ? column.legendId : generateUUID().replace(/-/g, ''),
        color: fieldColor ?? this.colorField,
        selectedColorPalette: columnColorPalette,
      };
    });
    if (!this.columns.length) {
      this.columns.push({
        addToText: false,
        legendId: generateUUID().replace(/-/g, ''),
        selected: {
          name: '',
          value: '',
        },
        highlighted: false,
        color: this.colorField,
        selectedColorPalette: this.selectedColorPalette,
      });
    }
    this.setAllColumns();
    this.checkApplyValidity();
    this.setColumnValidity();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  getColorPaletteFromColorProp(color: EncColor): string {
    if (color.values?.length && this.view) {
      const colorCompiledField = CompiledField.fromEncField(this.view, color.values[0]);
      const fieldColor = resolveFieldColor(colorCompiledField, this.fieldColors);
      if (fieldColor) {
        return fieldColor.colorPaletteToken;
      }
    }
    return this.currentColorPaletteToken || '';
  }

  getSelectedColorPaletteToken(series?: EncSeries): string {
    if (series) {
      return this.getColorPaletteFromColorProp(series.properties.color);
    } else if (this.encoding && this.encoding.marks.series.length) {
      return this.getColorPaletteFromColorProp(this.encoding.marks.series[0].properties.color);
    } else if (this.encoding) {
      return this.getColorPaletteFromColorProp(this.encoding.marks.properties.color);
    }

    return '';
  }

  getColumnFieldColor(column: ConditionalFormattingTypes.Column | undefined) {
    if (column && this.view) {
      // If this is a new add, check for a default color
      const encField = EncodingHelpers.findInEncoding(this.encoding, column.value);
      if (encField) {
        const compiled = CompiledField.fromEncField(this.view, encField);
        return resolveFieldColor(compiled, this.fieldColors);
      }
    }
  }

  onChangeColorField(event: MenuItem, index: number) {
    const removedValue = this.columns[index].selected?.value;
    if (removedValue) {
      this.columnsMap[removedValue] = false;
    }
    this.columns[index].selected = event as ConditionalFormattingTypes.Column;
    this.columnsMap[(event as ConditionalFormattingTypes.Column).value] = true;
    if (this.columns[index].selected) {
      const fieldColor = this.getColumnFieldColor(this.columns[index].selected);
      this.columns[index].color = fieldColor ?? this.columns[index].color;
      const palette = this.allColorPalettes.find((palette) => palette.token === fieldColor?.colorPaletteToken);
      this.columns[index].selectedColorPalette = palette ?? this.selectedColorPalette;
    }
    this.isChanged = true;
    this.setAllColumns();
    this.checkApplyValidity();
    this.setColumnValidity();
  }

  addMoreColumns() {
    if (!this.isAddColumnDisabled) {
      this.columns.push({
        addToText: false,
        legendId: generateUUID().replace(/-/g, ''),
        selected: {
          name: '',
          value: '',
        },
        highlighted: false,
        selectedColorPalette: this.selectedColorPalette,
        color: this.colorField,
      });
      this.isChanged = true;
      this.checkApplyValidity();
    }
    this.setColumnValidity();
  }

  removeColumn(index: number) {
    const removedValue = this.columns[index].selected?.value;
    if (removedValue) {
      this.columnsMap[removedValue] = false;
    }
    this.columns.splice(index, 1);
    this.setAllColumns();
    this.checkApplyValidity();
    this.setColumnValidity();
  }

  onCancel() {
    this.cancel.emit();
    this.dialogRef.close();
  }

  onApply(applyClicked = true) {
    this.apply.emit({
      colors: this.columns.filter((column): column is ConditionalFormattingTypes.Color => {
        return column.selected != null && column.selected.value !== '';
      }),
      applyClicked,
    });
  }

  onApplyAndClose() {
    this.onApply(false);
    this.dialogRef.close();
  }

  setAddToText(addToText: boolean, index: number) {
    this.columns[index].addToText = addToText;
  }

  checkApplyValidity() {
    this.isApplyDisabled = this.columns.filter((column) => column.selected && !column.selected.value).length > 0;
  }

  setAllColumns() {
    this.allColumns = this.data.allColumns.filter((col) => !this.columnsMap[col.value]);
  }

  setColumnValidity() {
    if (this.columns.length >= this.data.allColumns.length) {
      this.isAddColumnDisabled = true;
    } else if (this.data.allColumns.length >= 1) {
      this.isAddColumnDisabled = false;
    }
  }

  openModal(index: number) {
    if (this.view == null || this.model == null) {
      return;
    }
    const column = this.columns[index];
    if (column == null || column.selected == null) {
      return;
    }
    let colorField = EncodingHelpers.findInEncoding(this.encoding, column.selected?.value);
    if (colorField == null) {
      return;
    }

    // Up to the caller to make this continuous even though it is discrete in the encoding. In the future, we may need to
    // also configure dimensions and those would need to be discrete. The modal doesn't know how to handle it.
    colorField = {
      ...colorField,
      variable: {
        ...colorField.variable,
        type: VariableType.CONTINUOUS,
      },
    };

    const currentPaletteToken = this.getSelectedColorPaletteToken(this.encoding?.marks.series[0]);

    const fieldColors = addFieldColor(
      colorField,
      {
        colorPaletteToken: this.columns[index].selectedColorPalette?.token ?? currentPaletteToken,
        palette: this.columns[index].selectedColorPalette?.value?.split(',') ?? ['#F9E8FA', '#980E9B'],
        map: {},
        gradient: this.columns[index].color?.gradient,
      },
      this.fieldColors
    );

    if (this.colorsModalFacade && this.view) {
      const data: ColorsModalData = {
        palettes: this.allColorPalettes || [],
        currentPaletteToken:
          this.columns[index].selectedColorPalette?.token ?? this.currentColorPaletteToken ?? currentPaletteToken,
        formatDefaultColor: this.format ? computePalette(this.format)[0] : '#ff0000',
        queryRunToken: this.queryRunToken,
        vizToken: this.vizToken ?? '',
        view: this.view,
        encoding: this.encoding,
        colorField: colorField,
        fieldColors: fieldColors,
        renderModel: undefined,
        fieldFormats: this.fieldFormats,
        chartHasSplitLegend: false,
        isManualUpdateMode: false,
        savedColorTarget: 'background',
        isChildModal: true,
        hideRangeControls: true,
        hideSteps: true,
        hideStepLabels: false,
        applyColors: this.onColorFieldUpdate.bind(this),
        onCancel: () => {},
      };

      this.colorsModalFacade.open(data);
    }
  }

  onColorFieldUpdate(field: EncField, color: ChartTypes.FieldColor) {
    const selectedColorPalette = this.allColorPalettes.find((palette) => palette.token === color.colorPaletteToken);

    this.columns.forEach((column: any) => {
      if (column.selected.value === field.formula) {
        column.selectedColorPalette = {
          ...selectedColorPalette,
          token: color.colorPaletteToken,
          type:
            color.gradient?.gradientType === ChartTypes.GradientType.DIVERGING
              ? OrgTypes.ColorPaletteType.Divergent
              : OrgTypes.ColorPaletteType.Sequential,
          value: color.palette.join(','),
        };
        column.color = color;
      }
    });
  }
}
